/**
 * Buddy automation definitions
 * Phase 1: Rod Buddy and Runner Buddy
 */

import { BuddyDefinition, BuddyType, Quality } from '@/types';
import { BALANCE_CONFIG } from '../balance/balanceConfig';

/**
 * All buddies in the game
 */
export const BUDDY_DEFINITIONS: BuddyDefinition[] = [
  {
    id: 'rod_buddy',
    type: BuddyType.ROD_BUDDY,
    name: 'Fishing Buddy',
    description: 'A helpful friend who operates an extra fishing rod automatically.',

    cost: BALANCE_CONFIG.ROD_BUDDY_UNLOCK_COST,
    unlockCost: BALANCE_CONFIG.ROD_BUDDY_UNLOCK_COST,
    unlockRequirement: {
      totalCurrency: 150, // Need to have earned 150+ total
    },

    behavior: {
      interval: 5, // Fishes every 5 seconds
      fishingTimeMultiplier: BALANCE_CONFIG.ROD_BUDDY_FISHING_TIME_MULT,
      catchChanceReduction: BALANCE_CONFIG.ROD_BUDDY_CATCH_REDUCTION,
      qualityCap: Quality.RARE, // Can't catch Epic+
    },

    icon: '🧑‍🤝‍🧑',
    emoji: '🧑‍🤝‍🧑',
  },

  {
    id: 'runner_buddy',
    type: BuddyType.RUNNER_BUDDY,
    name: 'Runner Buddy',
    description: 'A speedy helper who sells your fish to the buyer automatically.',

    cost: BALANCE_CONFIG.RUNNER_BUDDY_UNLOCK_COST,
    unlockCost: BALANCE_CONFIG.RUNNER_BUDDY_UNLOCK_COST,
    unlockRequirement: {
      otherBuddyUnlocked: 'rod_buddy', // Unlock Rod Buddy first
    },

    behavior: {
      interval: BALANCE_CONFIG.RUNNER_BUDDY_RUN_INTERVAL,
      runInterval: BALANCE_CONFIG.RUNNER_BUDDY_RUN_INTERVAL,
      sellPriceMultiplier: BALANCE_CONFIG.RUNNER_BUDDY_PRICE_MULT,
      sellFilter: {
        maxQuality: Quality.RARE, // Only sells Rare and below
      },
    },

    icon: '🏃',
    emoji: '🏃',
  },

  // Future buddies (Phase 2+)
  // {
  //   id: 'processor_buddy',
  //   type: BuddyType.PROCESSOR_BUDDY,
  //   name: 'Fish Processor',
  //   description: 'Processes fish into higher-value products.',
  //   unlockCost: 500,
  //   behavior: {
  //     processingRate: 0.1, // 10% of fish get processed
  //     valueMultiplier: 2.0, // Processed fish worth 2x
  //   },
  //   icon: '🔪',
  // },
];

/**
 * Buddy lookup by ID (for fast access)
 */
const BUDDY_MAP = new Map(BUDDY_DEFINITIONS.map((buddy) => [buddy.id, buddy]));

/**
 * Get buddy definition by ID
 * @throws Error if buddy not found
 */
export function getBuddyDefinition(id: string): BuddyDefinition {
  const buddy = BUDDY_MAP.get(id);
  if (!buddy) {
    throw new Error(`Buddy not found: ${id}`);
  }
  return buddy;
}

/**
 * Get all buddies of a specific type
 */
export function getBuddiesByType(type: BuddyType): BuddyDefinition[] {
  return BUDDY_DEFINITIONS.filter((buddy) => buddy.type === type);
}

/**
 * Check if buddy ID exists
 */
export function buddyExists(id: string): boolean {
  return BUDDY_MAP.has(id);
}

/**
 * Get buddies in unlock order
 */
export function getBuddiesInOrder(): BuddyDefinition[] {
  // Rod Buddy first, then Runner Buddy
  return [...BUDDY_DEFINITIONS];
}

/**
 * Export as BUDDY_DATA for consistency
 */
export const BUDDY_DATA: Record<string, BuddyDefinition> = Object.fromEntries(
  BUDDY_DEFINITIONS.map((buddy) => [buddy.id, buddy])
);
